"use client";
import React, { useState, useEffect } from "react";
import clsx from "clsx";
import {
  format,
  addMonths,
  subMonths,
  addWeeks,
  subWeeks,
  addDays,
  subDays,
  startOfWeek,
  endOfWeek,
} from "date-fns";
import { enUS, es } from "date-fns/locale";
import { AnimatePresence, motion } from "framer-motion";
import { HiOutlineChevronLeft, HiOutlineChevronRight } from "react-icons/hi";
import { LuCirclePlus } from "react-icons/lu";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "react-hot-toast";
import AddAppointmentModal from "./AddApointmentModal";
import DayView from "./DayView";
import MonthView from "./MonthView";
import WeekView from "./WeekView";
import { PrimaryButton } from "../components/buttons/primaryButton/PrimaryButton.tsx";
import { getCurrencySymbol } from "../utils/currency/getCurrencySymbol";
import { useAppSelector } from "../hooks/redux/useRedux";
import { selectUser } from "../redux/slices/profileSlice/ProfileSlice";


const Calendar = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();

  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [direction, setDirection] = useState(0);
  const [view, setView] = useState("Month");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [dayEvents, setDayEvents] = useState([]);
  const [refreshKey, setRefreshKey] = useState(0);
  const [userCurrency, setUserCurrency] = useState("USD");
  const [userCurrencySymbol, setUserCurrencySymbol] = useState("$");

  const [summary, setSummary] = useState({
    price: 0,
    currency: "USD",
    currencySymbol: "$",
  });

  const token =
    localStorage.getItem("accessToken") ||
    sessionStorage.getItem("accessToken");

  const days = [
    t("home.headerDayMonday"),
    t("home.headerDayTuesday"),
    t("home.headerDayWednesday"),
    t("home.headerDayThursday"),
    t("home.headerDayFriday"),
    t("home.headerDaySaturday"),
    t("home.headerDaySunday"),
  ];

  const user = useAppSelector(selectUser);

  useEffect(() => {
    const currencyCode = user?.currency || "USD";
    const currencySymbol = getCurrencySymbol(currencyCode);

    setUserCurrency(currencyCode);
    setUserCurrencySymbol(currencySymbol);

    setSummary((prev) => ({
      ...prev,
      currency: currencyCode,
      currencySymbol,
    }));
  }, [user]);

  useEffect(() => {
    if (!token) {
      toast.error(t("home.errorAuthorizationRequired"));
      navigate("/sign-in");
    }
  }, [token, navigate]);

  const handlePrev = () => {
    setDirection(-1);
    if (view === "Month") setCurrentDate(subMonths(currentDate, 1));
    if (view === "Week") setSelectedDate(subWeeks(selectedDate, 1));
    if (view === "Day") setSelectedDate((p) => subDays(p, 1));
  };

  const handleNext = () => {
    setDirection(1);
    if (view === "Month") setCurrentDate(addMonths(currentDate, 1));
    if (view === "Week") setSelectedDate(addWeeks(selectedDate, 1));
    if (view === "Day") setSelectedDate((p) => addDays(p, 1));
  };

  const dateLocale = i18n.language?.startsWith("es") ? es : enUS;

  const capitalize = (str) =>
    str ? str.charAt(0).toUpperCase() + str.slice(1) : "";

  const renderDateLabel = () => {
    if (view === "Week") {
      const start = startOfWeek(selectedDate, { weekStartsOn: 1 });
      const end = endOfWeek(selectedDate, { weekStartsOn: 1 });
      return `${format(start, "d MMM", { locale: dateLocale })} - ${format(
        end,
        "d MMM yyyy",
        { locale: dateLocale },
      )}`;
    }
    if (view === "Day") {
      return i18n.language?.startsWith("es")
        ? format(selectedDate, "d 'de' MMMM, yyyy", { locale: dateLocale })
        : format(selectedDate, "MMMM d, yyyy", { locale: dateLocale });
    }
    return capitalize(format(currentDate, "MMMM yyyy", { locale: dateLocale }));
  };

  const renderDayHeaders = () => (
    <div className="grid grid-cols-7 min-w-[640px] md:min-w-0 px-1 pb-3 border-b border-[#E5E7EB] bg-[#F6F6F6] rounded-t-2xl py-3">
      {days.map((day) => (
        <div key={day} className="text-center text-[14px] text-[#302B2B]">
          {day}
        </div>
      ))}
    </div>
  );

  const handleSummaryChange = (newSummary) => {
    const currencyCode = newSummary.currency || userCurrency || "USD";
    const currencySymbol = getCurrencySymbol(currencyCode);

    setSummary({
      price: newSummary.price || 0,
      currency: currencyCode,
      currencySymbol: currencySymbol,
    });
  };

  const formatSummaryPrice = (price) => {
    try {
      const priceNum = typeof price === "string" ? parseFloat(price) : price;
      if (isNaN(priceNum)) return "0.00";

      return priceNum.toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    } catch (error) {
      console.error("Error formatting summary price:", error);
      return "0.00";
    }
  };

  return (
    <div className="w-full mx-auto bg-white rounded-[20px] p-6 shadow border border-gray-200">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6 w-full">
        <div className="text-[34px] font-normal w-full md:w-auto md:shrink-0">
          {summary.currencySymbol}
          {formatSummaryPrice(summary.price)}
        </div>

        <div className="flex items-center border rounded-full w-full max-w-[290px] h-[60px] px-5 justify-between">
          {[
            { value: "Day", label: t("home.buttonViewDay") },
            { value: "Week", label: t("home.buttonViewWeek") },
            { value: "Month", label: t("home.buttonViewMonth") },
          ].map((v) => (
            <PrimaryButton
              key={v.value}
              onClick={() => setView(v.value)}
              variant={view === v.value ? "standard" : "ghost"}
              className="!rounded-full"
              label={v.label}
            />
          ))}
        </div>

        <PrimaryButton
          onClick={() => setIsModalOpen(true)}
          label={t("home.buttonAddAppointment")}
          rightIcon={<LuCirclePlus className="text-2xl" />}
        />
      </div>

      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-4">
          <PrimaryButton
            onClick={handlePrev}
            variant="ghost"
            size="small"
            label=""
            leftIcon={<HiOutlineChevronLeft className="text-[#302B2B] text-[20px]" />}
            className="!p-2 text-[#302B2B]"
          />
          <span className="text-[20px] text-[#302B2B] capitalize">
            {renderDateLabel()}
          </span>
          <PrimaryButton
            onClick={handleNext}
            variant="ghost"
            size="small"
            label=""
            leftIcon={<HiOutlineChevronRight className="text-[#302B2B] text-[20px]" />}
            className="!p-2 text-[#302B2B]"
          />
        </div>
      </div>

      <div className="overflow-x-auto">
      {view !== "Day" && renderDayHeaders()}

      <AnimatePresence mode="wait">
        <motion.div
          key={`${view}-${refreshKey}`}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          transition={{ duration: 0.25 }}
        >
          {view === "Month" && (
            <MonthView
              token={token}
              currentDate={currentDate}
              selectedDate={selectedDate}
              direction={direction}
              setSelectedDate={setSelectedDate}
              setDayEvents={setDayEvents}
              refreshKey={refreshKey}
              onSummaryChange={handleSummaryChange}
              userCurrency={userCurrency}
            />
          )}

          {view === "Week" && (
            <WeekView
              token={token}
              selectedDate={selectedDate}
              setSelectedDate={setSelectedDate}
              setDayEvents={setDayEvents}
              onSummaryChange={handleSummaryChange}
              refreshKey={refreshKey}
              userCurrency={userCurrency}
            />
          )}

          {view === "Day" && (
            <DayView
              token={token}
              date={format(selectedDate, "yyyy-MM-dd")}
              onDateChange={setSelectedDate}
              setRefreshKey={setRefreshKey}
              onSummaryChange={handleSummaryChange}
              userCurrency={userCurrency}
            />
          )}
        </motion.div>
      </AnimatePresence>
      </div>

      <AddAppointmentModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSuccess={() => setRefreshKey((prev) => prev + 1)}
      />
    </div>
  );
};

export default Calendar;
